import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, RotateCcw, Plus, Trash2 } from "lucide-react";

const BoatCurrentSimulator = () => {
  // 状态管理
  const [boatPosition, setBoatPosition] = useState({ x: 0, y: 0 });
  const [boatSpeed, setBoatSpeed] = useState(1); // km/h
  const [currentDirection, setCurrentDirection] = useState("east"); // 东南西北
  const [currentSpeed, setCurrentSpeed] = useState(0.333); // km/h
  const [timeScale, setTimeScale] = useState(600); // 1小时 = 6秒
  const [isRunning, setIsRunning] = useState(false);
  const [showTrajectory, setShowTrajectory] = useState(true);
  const [trajectory, setTrajectory] = useState([]);
  const [instructions, setInstructions] = useState([
    { direction: "south", distance: 1, completed: false }, // 南 1km
    { direction: "east", distance: 1, completed: false }, // 东 1km
    { direction: "north", distance: 1, completed: false }, // 北 1km
  ]);
  const [currentInstructionIndex, setCurrentInstructionIndex] = useState(0);
  const [instructionProgress, setInstructionProgress] = useState(0);
  const [simulationTime, setSimulationTime] = useState(0);

  // 新指令输入
  const [newDirection, setNewDirection] = useState("north");
  const [newDistance, setNewDistance] = useState("");

  const animationRef = useRef();
  const lastTimeRef = useRef(0);
  const waveAnimationTime = useRef(0);

  // 方向定义 - 自然坐标系
  const directions = {
    north: { angle: 0, name: "北", vector: { x: 0, y: 1 } },
    east: { angle: 90, name: "东", vector: { x: 1, y: 0 } },
    south: { angle: 180, name: "南", vector: { x: 0, y: -1 } },
    west: { angle: 270, name: "西", vector: { x: -1, y: 0 } },
  };

  // 获取方向向量
  const getDirectionVector = (direction) => {
    return directions[direction]?.vector || { x: 0, y: 0 };
  };

  // 重置模拟
  const resetSimulation = () => {
    setIsRunning(false);
    setBoatPosition({ x: 0, y: 0 });
    setTrajectory([]);
    setCurrentInstructionIndex(0);
    setInstructionProgress(0);
    setSimulationTime(0);
    waveAnimationTime.current = 0;
    setInstructions((prev) =>
      prev.map((inst) => ({ ...inst, completed: false }))
    );
  };

  // 添加新指令
  const addInstruction = () => {
    if (newDistance !== "") {
      const distance = parseFloat(newDistance);
      if (!isNaN(distance) && distance > 0) {
        setInstructions((prev) => [
          ...prev,
          { direction: newDirection, distance, completed: false },
        ]);
        setNewDistance("");
      }
    }
  };

  // 删除指令
  const removeInstruction = (index) => {
    setInstructions((prev) => prev.filter((_, i) => i !== index));
    if (index <= currentInstructionIndex) {
      setCurrentInstructionIndex(Math.max(0, currentInstructionIndex - 1));
    }
  };

  // 动画循环
  useEffect(() => {
    if (!isRunning) return;

    const animate = (currentTime) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = currentTime;
      }

      const deltaTime = (currentTime - lastTimeRef.current) / 1000; // 秒
      const simulationDelta = deltaTime * timeScale; // 模拟时间（秒）

      setSimulationTime((prev) => prev + simulationDelta);
      waveAnimationTime.current += deltaTime;

      if (
        currentInstructionIndex < instructions.length &&
        !instructions[currentInstructionIndex].completed
      ) {
        const currentInstruction = instructions[currentInstructionIndex];

        // 船只目标速度向量
        const boatVector = getDirectionVector(currentInstruction.direction);
        const boatVx = boatVector.x * boatSpeed;
        const boatVy = boatVector.y * boatSpeed;

        // 洋流速度向量
        const currentVector = getDirectionVector(currentDirection);
        const currentVx = currentVector.x * currentSpeed;
        const currentVy = currentVector.y * currentSpeed;

        // 合速度
        const totalVx = boatVx + currentVx;
        const totalVy = boatVy + currentVy;

        // 计算移动距离（船只相对于水的移动距离，用于计算指令完成度）
        const boatMoveDistance = (boatSpeed * simulationDelta) / 3600; // km

        setBoatPosition((prev) => {
          const newX = prev.x + (totalVx * simulationDelta) / 3600;
          const newY = prev.y + (totalVy * simulationDelta) / 3600;

          // 更新轨迹
          setTrajectory((traj) => [...traj, { x: newX, y: newY }]);

          return { x: newX, y: newY };
        });

        // 更新指令进度（基于船只相对于水的移动）
        setInstructionProgress((prev) => {
          const newProgress = prev + boatMoveDistance;
          if (newProgress >= currentInstruction.distance) {
            // 完成当前指令
            setInstructions((prevInst) =>
              prevInst.map((inst, i) =>
                i === currentInstructionIndex
                  ? { ...inst, completed: true }
                  : inst
              )
            );
            setCurrentInstructionIndex((prevIndex) => prevIndex + 1);
            return 0;
          }
          return newProgress;
        });
      } else if (currentInstructionIndex >= instructions.length) {
        // 所有指令完成
        setIsRunning(false);
      }

      lastTimeRef.current = currentTime;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [
    isRunning,
    currentInstructionIndex,
    instructions,
    boatSpeed,
    currentDirection,
    currentSpeed,
    timeScale,
  ]);

  // 切换运行状态
  const toggleRunning = () => {
    setIsRunning(!isRunning);
    if (!isRunning) {
      lastTimeRef.current = 0;
    }
  };

  // 生成洋流波浪线
  const generateCurrentWaves = () => {
    const waves = [];
    const currentVector = getDirectionVector(currentDirection);
    const animOffset = waveAnimationTime.current * 2; // 动画速度

    // 在可视区域内生成洋流波浪线
    for (let i = 0; i < 8; i++) {
      const baseX = (i - 4) * 0.4;
      const baseY = Math.sin(i + animOffset) * 0.5;

      // 根据洋流方向调整波浪线的位置和方向
      let startX, startY, endX, endY;

      if (currentDirection === "east" || currentDirection === "west") {
        // 水平流动
        startX = currentDirection === "east" ? -1.5 : 1.5;
        endX = currentDirection === "east" ? 1.5 : -1.5;
        startY = endY = baseX;
      } else {
        // 垂直流动
        startY = currentDirection === "north" ? -1.5 : 1.5;
        endY = currentDirection === "north" ? 1.5 : -1.5;
        startX = endX = baseX;
      }

      // 创建波浪路径
      const wavePoints = [];
      const segments = 20;
      for (let j = 0; j <= segments; j++) {
        const t = j / segments;
        const x = startX + (endX - startX) * t;
        const y = startY + (endY - startY) * t;

        // 添加波浪效果
        const waveOffset = Math.sin(t * 6 + animOffset) * 0.05;
        if (currentDirection === "east" || currentDirection === "west") {
          wavePoints.push(`${x},${-(y + waveOffset)}`);
        } else {
          wavePoints.push(`${x + waveOffset},${-y}`);
        }
      }

      waves.push({
        id: i,
        path: wavePoints.join(" "),
        opacity: 0.3 + Math.sin(animOffset + i) * 0.2,
      });
    }

    return waves;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-center mb-8 text-blue-800">
        小船洋流运动模拟器
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 控制面板 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 基本参数 */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">
              基本参数
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    小船速度
                  </label>
                  <div className="flex items-center space-x-1">
                    <input
                      type="number"
                      value={boatSpeed}
                      onChange={(e) =>
                        setBoatSpeed(parseFloat(e.target.value) || 1)
                      }
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      step="0.1"
                      min="0.1"
                    />
                    <span className="text-xs text-gray-500">km/h</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    时间倍速
                  </label>
                  <input
                    type="number"
                    value={timeScale}
                    onChange={(e) =>
                      setTimeScale(parseInt(e.target.value) || 600)
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    step="100"
                    min="100"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                1小时 = {(timeScale / 60).toFixed(1)}秒
              </p>
            </div>
          </div>

          {/* 洋流参数 */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">
              洋流参数
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  洋流方向
                </label>
                <select
                  value={currentDirection}
                  onChange={(e) => setCurrentDirection(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="north">北 ↑</option>
                  <option value="east">东 →</option>
                  <option value="south">南 ↓</option>
                  <option value="west">西 ←</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  洋流速度
                </label>
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    value={currentSpeed}
                    onChange={(e) =>
                      setCurrentSpeed(parseFloat(e.target.value) || 0.333)
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    step="0.1"
                    min="0"
                  />
                  <span className="text-xs text-gray-500">km/h</span>
                </div>
              </div>
            </div>
          </div>

          {/* 移动指令 */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-3 text-gray-800">
              移动指令
            </h3>

            {/* 添加新指令 */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select
                  value={newDirection}
                  onChange={(e) => setNewDirection(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="north">北 ↑</option>
                  <option value="east">东 →</option>
                  <option value="south">南 ↓</option>
                  <option value="west">西 ←</option>
                </select>
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    placeholder="距离"
                    value={newDistance}
                    onChange={(e) => setNewDistance(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    min="0.1"
                    step="0.1"
                  />
                  <span className="text-xs text-gray-500">km</span>
                </div>
              </div>
              <button
                onClick={addInstruction}
                className="w-full px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center justify-center space-x-1"
              >
                <Plus size={14} />
                <span>添加指令</span>
              </button>
            </div>

            {/* 指令列表 */}
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {instructions.map((instruction, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg border ${
                    instruction.completed
                      ? "bg-green-50 border-green-200"
                      : index === currentInstructionIndex
                      ? "bg-blue-50 border-blue-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm">
                      {directions[instruction.direction]?.name}{" "}
                      {instruction.distance}km
                    </span>
                    <button
                      onClick={() => removeInstruction(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {index === currentInstructionIndex &&
                    !instruction.completed && (
                      <div className="mt-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${
                                (instructionProgress / instruction.distance) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {instructionProgress.toFixed(2)}/
                          {instruction.distance}km
                        </p>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>

          {/* 控制按钮 */}
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex space-x-2">
              <button
                onClick={toggleRunning}
                className={`flex-1 px-4 py-2 rounded-md text-white font-medium flex items-center justify-center space-x-2 ${
                  isRunning
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isRunning ? <Pause size={20} /> : <Play size={20} />}
                <span>{isRunning ? "暂停" : "开始"}</span>
              </button>

              <button
                onClick={resetSimulation}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center space-x-2"
              >
                <RotateCcw size={20} />
                <span>重置</span>
              </button>
            </div>

            <div className="mt-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showTrajectory}
                  onChange={(e) => setShowTrajectory(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">显示运动轨迹</span>
              </label>
            </div>
          </div>
        </div>

        {/* 可视化区域 */}
        <div className="lg:col-span-2">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">运动轨迹</h3>
              <div className="text-sm text-gray-600">
                模拟时间: {Math.floor(simulationTime / 3600)}h{" "}
                {Math.floor((simulationTime % 3600) / 60)}m
              </div>
            </div>

            <div
              className="relative bg-blue-50 rounded-lg overflow-hidden"
              style={{ height: "500px" }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="-1.5 -1.5 3 3"
                className="border border-blue-200"
              >
                <defs>
                  {/* 箭头标记 */}
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#f59e0b" />
                  </marker>
                </defs>

                {/* 坐标轴 */}
                <line
                  x1="-1.5"
                  y1="0"
                  x2="1.5"
                  y2="0"
                  stroke="#64748b"
                  strokeWidth="0.02"
                />
                <line
                  x1="0"
                  y1="-1.5"
                  x2="0"
                  y2="1.5"
                  stroke="#64748b"
                  strokeWidth="0.02"
                />

                {/* 坐标刻度和标签 */}
                {[-1, -0.5, 0.5, 1].map((i) => (
                  <g key={i}>
                    <line
                      x1={i}
                      y1="-0.05"
                      x2={i}
                      y2="0.05"
                      stroke="#64748b"
                      strokeWidth="0.01"
                    />
                    <line
                      x1="-0.05"
                      y1={i}
                      x2="0.05"
                      y2={i}
                      stroke="#64748b"
                      strokeWidth="0.01"
                    />
                    {i !== 0 && (
                      <>
                        <text
                          x={i}
                          y="-0.1"
                          fontSize="0.08"
                          textAnchor="middle"
                          fill="#64748b"
                        >
                          {i}km
                        </text>
                        <text
                          x="0.1"
                          y={i + 0.03}
                          fontSize="0.08"
                          textAnchor="start"
                          fill="#64748b"
                        >
                          {-i}km
                        </text>
                      </>
                    )}
                  </g>
                ))}

                {/* 方向标识 */}
                <text
                  x="0"
                  y="-1.35"
                  fontSize="0.1"
                  textAnchor="middle"
                  fill="#1f2937"
                  fontWeight="bold"
                >
                  北
                </text>
                <text
                  x="1.35"
                  y="0.03"
                  fontSize="0.1"
                  textAnchor="middle"
                  fill="#1f2937"
                  fontWeight="bold"
                >
                  东
                </text>
                <text
                  x="0"
                  y="1.45"
                  fontSize="0.1"
                  textAnchor="middle"
                  fill="#1f2937"
                  fontWeight="bold"
                >
                  南
                </text>
                <text
                  x="-1.35"
                  y="0.03"
                  fontSize="0.1"
                  textAnchor="middle"
                  fill="#1f2937"
                  fontWeight="bold"
                >
                  西
                </text>

                {/* 洋流波浪线效果 */}
                {currentSpeed > 0 &&
                  generateCurrentWaves().map((wave) => (
                    <polyline
                      key={wave.id}
                      points={wave.path}
                      fill="none"
                      stroke="#06b6d4"
                      strokeWidth="0.01"
                      opacity={wave.opacity}
                    />
                  ))}

                {/* 洋流方向指示器 */}
                <g transform={`translate(1.1, 1.1)`}>
                  <rect
                    x="-0.25"
                    y="-0.15"
                    width="0.5"
                    height="0.3"
                    rx="0.03"
                    fill="white"
                    stroke="#06b6d4"
                    strokeWidth="0.01"
                    opacity="0.9"
                  />
                  <text
                    x="0"
                    y="-0.05"
                    fontSize="0.06"
                    textAnchor="middle"
                    fill="#06b6d4"
                    fontWeight="bold"
                  >
                    洋流
                  </text>
                  <text
                    x="0"
                    y="0.05"
                    fontSize="0.05"
                    textAnchor="middle"
                    fill="#06b6d4"
                  >
                    {directions[currentDirection]?.name} {currentSpeed}km/h
                  </text>

                  {/* 洋流主方向箭头 */}
                  {currentSpeed > 0 && (
                    <g transform={`translate(0, 0.12)`}>
                      <line
                        x1="0"
                        y1="0"
                        x2={getDirectionVector(currentDirection).x * 0.15}
                        y2={-getDirectionVector(currentDirection).y * 0.15}
                        stroke="#06b6d4"
                        strokeWidth="0.02"
                      />
                      <polygon
                        points={`${
                          getDirectionVector(currentDirection).x * 0.15
                        },${-getDirectionVector(currentDirection).y * 0.15} ${
                          getDirectionVector(currentDirection).x * 0.1 -
                          getDirectionVector(currentDirection).y * 0.02
                        },${
                          -getDirectionVector(currentDirection).y * 0.1 -
                          getDirectionVector(currentDirection).x * 0.02
                        } ${
                          getDirectionVector(currentDirection).x * 0.1 +
                          getDirectionVector(currentDirection).y * 0.02
                        },${
                          -getDirectionVector(currentDirection).y * 0.1 +
                          getDirectionVector(currentDirection).x * 0.02
                        }`}
                        fill="#06b6d4"
                      />
                    </g>
                  )}
                </g>

                {/* 轨迹线 */}
                {showTrajectory && trajectory.length > 1 && (
                  <polyline
                    points={trajectory
                      .map((point) => `${point.x},${-point.y}`)
                      .join(" ")}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="0.02"
                    opacity="0.8"
                  />
                )}

                {/* 起点 */}
                <circle
                  cx="0"
                  cy="0"
                  r="0.03"
                  fill="#22c55e"
                  stroke="white"
                  strokeWidth="0.01"
                />
                <text
                  x="0"
                  y="-0.08"
                  fontSize="0.06"
                  textAnchor="middle"
                  fill="#22c55e"
                  fontWeight="bold"
                >
                  起点
                </text>

                {/* 小船当前位置 */}
                <g
                  transform={`translate(${boatPosition.x}, ${-boatPosition.y})`}
                >
                  <circle
                    cx="0"
                    cy="0"
                    r="0.04"
                    fill="#f59e0b"
                    stroke="white"
                    strokeWidth="0.01"
                  />
                  <text
                    x="0"
                    y="0.12"
                    fontSize="0.06"
                    textAnchor="middle"
                    fill="#f59e0b"
                    fontWeight="bold"
                  >
                    小船
                  </text>

                  {/* 小船移动方向指示 */}
                  {currentInstructionIndex < instructions.length && (
                    <g>
                      <line
                        x1="0"
                        y1="0"
                        x2={
                          getDirectionVector(
                            instructions[currentInstructionIndex].direction
                          ).x * 0.1
                        }
                        y2={
                          -getDirectionVector(
                            instructions[currentInstructionIndex].direction
                          ).y * 0.1
                        }
                        stroke="#f59e0b"
                        strokeWidth="0.015"
                        markerEnd="url(#arrowhead)"
                      />
                    </g>
                  )}
                </g>

                {/* 轨迹点 */}
                {showTrajectory &&
                  trajectory.map(
                    (point, index) =>
                      index % 30 === 0 &&
                      index > 0 && (
                        <circle
                          key={index}
                          cx={point.x}
                          cy={-point.y}
                          r="0.01"
                          fill="#ef4444"
                          opacity="0.6"
                        />
                      )
                  )}
              </svg>
            </div>

            {/* 状态信息 */}
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 p-3 rounded">
                <h4 className="font-medium text-gray-700 mb-1">当前位置</h4>
                <p>
                  东西: {boatPosition.x > 0 ? "东" : "西"}{" "}
                  {Math.abs(boatPosition.x).toFixed(3)} km
                </p>
                <p>
                  南北: {boatPosition.y > 0 ? "北" : "南"}{" "}
                  {Math.abs(boatPosition.y).toFixed(3)} km
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <h4 className="font-medium text-gray-700 mb-1">当前指令</h4>
                {currentInstructionIndex < instructions.length ? (
                  <p>
                    向
                    {
                      directions[
                        instructions[currentInstructionIndex].direction
                      ]?.name
                    }{" "}
                    {instructions[currentInstructionIndex].distance}km
                  </p>
                ) : (
                  <p className="text-green-600">所有指令完成</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  洋流影响: {directions[currentDirection]?.name} {currentSpeed}
                  km/h
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default BoatCurrentSimulator;
